import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExternalLinkAlt,
  faPause,
  faPlay,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { API_BASE, getMiniCrates } from "./miniCrates";
import "./DiscoverZineStrip.scss";

const loadingSection = {
  id: "loading-section",
  date: "",
  dayLabel: "Loading",
  title: "Fresh Crates",
  theme: "public discover strip",
  summary: "New selections are landing now.",
  sortOrder: 0,
};

const sourceBadgeLabels = {
  "sb-vibe": "SB-VIBE",
  "staff-pick": "Staff Pick",
  guest: "Guest",
  "user-submission": "User Submission",
};

function formatPlatform(platform) {
  return platform
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatTag(tag) {
  return tag.replaceAll("-", " ");
}

function formatSectionDate(date) {
  const parsedDate = new Date(`${date}T00:00:00`);

  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
  }).format(parsedDate);
}

function formatDuration(totalSeconds) {
  if (!totalSeconds) {
    return null;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

function getCrateDuration(crate) {
  const totalSeconds = crate.tracks.reduce(
    (total, track) => total + (track.durationSeconds || 0),
    0
  );

  if (!totalSeconds) {
    return null;
  }

  return `~${Math.max(1, Math.round(totalSeconds / 60))} min`;
}

function resolvePreviewUrl(previewUrl) {
  if (!previewUrl) {
    return null;
  }

  if (previewUrl.startsWith("/api/")) {
    return `${API_BASE}${previewUrl}`;
  }

  return previewUrl;
}

function getTrackPlaybackUrl(track) {
  return resolvePreviewUrl(track.previewUrl || track.previewEndpoint);
}

function getSafeExternalUrl(url) {
  if (!url) {
    return null;
  }

  try {
    const parsed = new URL(url);

    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }

    return parsed.href;
  } catch {
    return null;
  }
}

function getErrorMessage(error) {
  if (error instanceof Error) {
    return error.message;
  }

  return "Unknown loading error.";
}

function groupCratesBySection(result) {
  if (!result) {
    return [];
  }

  const cratesBySection = new Map();

  result.crates.forEach((crate) => {
    const crates = cratesBySection.get(crate.sectionId) || [];
    crates.push(crate);
    cratesBySection.set(crate.sectionId, crates);
  });

  return [...result.sections]
    .sort((first, second) => first.sortOrder - second.sortOrder)
    .map((section) => ({
      section,
      crates: cratesBySection.get(section.id) || [],
    }))
    .filter((group) => group.crates.length > 0);
}

function shouldReduceMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
}

const GlideText = ({
  as: Component = "span",
  children,
  className = "",
  text,
}) => {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [glideState, setGlideState] = useState({
    distance: 0,
    duration: 0,
    isOverflowing: false,
  });
  const textValue =
    text ||
    (typeof children === "string" || typeof children === "number"
      ? String(children)
      : undefined);

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;

    if (!outer || !inner) {
      return undefined;
    }

    const measure = () => {
      const distance = Math.ceil(inner.scrollWidth - outer.clientWidth);
      const isOverflowing = distance > 4;
      const nextState = {
        distance: isOverflowing ? distance + 18 : 0,
        duration: isOverflowing
          ? Math.min(15, Math.max(6, (distance + 80) / 28))
          : 0,
        isOverflowing,
      };

      setGlideState((previousState) =>
        previousState.distance === nextState.distance &&
        previousState.duration === nextState.duration &&
        previousState.isOverflowing === nextState.isOverflowing
          ? previousState
          : nextState
      );
    };

    measure();

    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;

    resizeObserver?.observe(outer);
    resizeObserver?.observe(inner);
    window.addEventListener("resize", measure);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [textValue]);

  return (
    <Component
      className={`zine-glide-text ${className}`.trim()}
      data-overflow={glideState.isOverflowing}
      ref={outerRef}
      style={
        glideState.isOverflowing
          ? {
              "--glide-distance": `-${glideState.distance}px`,
              "--glide-duration": `${glideState.duration}s`,
            }
          : undefined
      }
      title={glideState.isOverflowing ? textValue : undefined}
    >
      <span className="zine-glide-text-inner" ref={innerRef}>
        {children}
      </span>
    </Component>
  );
};

const DiscoverZineStrip = () => {
  const [selectedSources, setSelectedSources] = useState([]);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [selectedCrate, setSelectedCrate] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackState, setPlaybackState] = useState({
    currentTime: 0,
    duration: 0,
    progress: 0,
  });
  const [scrollProgress, setScrollProgress] = useState(0);
  const audioRef = useRef(new Audio());

  useEffect(() => {
    let isActive = true;

    async function loadMiniCrates() {
      setStatus("loading");
      setErrorMessage(null);

      try {
        const nextResult = await getMiniCrates({
          sources: selectedSources.length > 0 ? selectedSources : undefined,
        });

        if (!isActive) {
          return;
        }

        setResult(nextResult);
        setStatus("ready");
      } catch (error) {
        if (!isActive) {
          return;
        }

        setErrorMessage(getErrorMessage(error));
        setStatus("error");
      }
    }

    loadMiniCrates();

    return () => {
      isActive = false;
    };
  }, [loadAttempt, selectedSources]);

  useEffect(() => {
    const audio = audioRef.current;
    const updatePlaybackState = () => {
      const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
      const currentTime = audio.currentTime || 0;

      setPlaybackState({
        currentTime,
        duration,
        progress: duration ? (currentTime / duration) * 100 : 0,
      });
    };
    const onEnded = () => {
      setIsPlaying(false);
      updatePlaybackState();
    };

    audio.addEventListener("ended", onEnded);
    audio.addEventListener("timeupdate", updatePlaybackState);
    audio.addEventListener("loadedmetadata", updatePlaybackState);

    return () => {
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("timeupdate", updatePlaybackState);
      audio.removeEventListener("loadedmetadata", updatePlaybackState);
      audio.pause();
      audio.src = "";
    };
  }, []);

  useEffect(() => {
    const scrollEl = document.querySelector(".App-content") || window;
    const getMetrics = () => {
      if (scrollEl === window) {
        const doc = document.documentElement;
        return {
          scrollTop: doc.scrollTop || document.body.scrollTop || 0,
          scrollHeight: doc.scrollHeight,
          clientHeight: doc.clientHeight,
        };
      }

      return {
        scrollTop: scrollEl.scrollTop,
        scrollHeight: scrollEl.scrollHeight,
        clientHeight: scrollEl.clientHeight,
      };
    };
    const updateScrollProgress = () => {
      const { scrollTop, scrollHeight, clientHeight } = getMetrics();
      const maxScroll = Math.max(scrollHeight - clientHeight, 1);
      setScrollProgress(Math.min(100, Math.max(0, (scrollTop / maxScroll) * 100)));
    };

    updateScrollProgress();
    scrollEl.addEventListener("scroll", updateScrollProgress, { passive: true });
    window.addEventListener("resize", updateScrollProgress);

    return () => {
      scrollEl.removeEventListener("scroll", updateScrollProgress);
      window.removeEventListener("resize", updateScrollProgress);
    };
  }, []);

  const sourceOptions = useMemo(() => result?.sourceOptions || [], [result]);
  const sourceOptionById = useMemo(
    () => new Map(sourceOptions.map((sourceOption) => [sourceOption.id, sourceOption])),
    [sourceOptions]
  );
  const sectionGroups = useMemo(() => groupCratesBySection(result), [result]);
  const isLoading = status === "loading";
  const isEmpty = status === "ready" && sectionGroups.length === 0;
  const visibleCrateCount = result?.crates.length || 0;

  useEffect(() => {
    if (!selectedCrate || !result?.crates) {
      return;
    }

    if (!result.crates.some((crate) => crate.id === selectedCrate.id)) {
      setSelectedCrate(null);
    }
  }, [result, selectedCrate]);

  const selectedSourceLabel = useMemo(() => {
    if (selectedSources.length === 0) {
      return "all sources";
    }

    if (selectedSources.length === 1) {
      const selectedSource = selectedSources[0];
      return sourceOptionById.get(selectedSource)?.label || selectedSource.replace(/-/g, " ");
    }

    return `${selectedSources.length} selected sources`;
  }, [selectedSources, sourceOptionById]);

  const handleTrackPreview = useCallback(
    (track, crate) => {
      const audio = audioRef.current;
      const previewUrl = getTrackPlaybackUrl(track);

      if (!previewUrl) {
        return;
      }

      if (currentTrack?.id === track.id && isPlaying) {
        audio.pause();
        setIsPlaying(false);
        return;
      }

      audio.src = previewUrl;
      setPlaybackState({
        currentTime: 0,
        duration: 0,
        progress: 0,
      });
      audio.play().catch(() => {});
      setCurrentTrack({
        ...track,
        crateId: crate.id,
        crateTitle: crate.title,
        artworkUrl: track.artworkUrl || crate.artwork.url,
      });
      setIsPlaying(true);
    },
    [currentTrack, isPlaying]
  );

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;

    if (!currentTrack) {
      return;
    }

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    audio.play().catch(() => {});
    setIsPlaying(true);
  }, [currentTrack, isPlaying]);

  const stopPreview = useCallback(() => {
    const audio = audioRef.current;
    audio.pause();
    audio.src = "";
    setCurrentTrack(null);
    setIsPlaying(false);
    setPlaybackState({
      currentTime: 0,
      duration: 0,
      progress: 0,
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedSources([]);
  }, []);

  const openCrateModal = useCallback((crate) => {
    setSelectedCrate(crate);
  }, []);

  const closeCrateModal = useCallback(() => {
    setSelectedCrate(null);
  }, []);

  return (
    <div className="discover-zine-page">
      <div
        className="zine-scrollbar"
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />
      <div className="discover-zine-shell">
        <header className="zine-hero">
          <div className="zine-hero-copy">
            <p className="zine-kicker">Spinnin' Beans</p>
            <h1>Discover</h1>
            <p className="zine-summary">
              Mini-crates from the underground lane: dusty house, leftfield club
              pressure, guest detours, and the odd little record that keeps ringing
              after the weekend.
            </p>
          </div>

          <div className="zine-hero-meta">
            <span>Now showing</span>
            <strong>{isLoading ? "Loading crates" : `${visibleCrateCount} crates`}</strong>
          </div>
        </header>

        <div className="zine-filter-zone">
          {sourceOptions.length > 0 ? (
            <SourceFilterBar
              selectedSources={selectedSources}
              sourceOptions={sourceOptions}
              onSelectedSourcesChange={setSelectedSources}
              isLoading={isLoading}
            />
          ) : (
            <FilterBarSkeleton />
          )}
        </div>

        <main className="zine-crate-strip" aria-busy={isLoading}>
          {status === "error" ? (
            <ErrorState
              message={errorMessage}
              onRetry={() => setLoadAttempt((attempt) => attempt + 1)}
            />
          ) : null}

          {isLoading ? <LoadingSections sections={result?.sections} /> : null}

          {isEmpty ? (
            <EmptyState
              selectedSourceLabel={selectedSourceLabel}
              onClearFilters={handleClearFilters}
            />
          ) : null}

          {status === "ready"
            ? sectionGroups.map(({ section, crates }) => (
                <section className="zine-section" key={section.id}>
                  <SectionHeader section={section} crateCount={crates.length} />
                  <div className="zine-section-grid">
                    {crates.map((crate, index) => (
                      <MiniCrateCard
                        activeTrackId={currentTrack?.id}
                        activeTrackProgress={playbackState.progress}
                        crate={crate}
                        index={index}
                        isPlaying={isPlaying && currentTrack?.crateId === crate.id}
                        key={crate.id}
                        onOpenCrate={openCrateModal}
                        onTrackPreview={handleTrackPreview}
                      />
                    ))}
                  </div>
                </section>
              ))
            : null}
        </main>
      </div>

      {createPortal(
        <>
          <ZineCrateModal
            activeTrackId={currentTrack?.id}
            activeTrackProgress={playbackState.progress}
            crate={selectedCrate}
            isPlaying={isPlaying}
            onClose={closeCrateModal}
            onTrackPreview={handleTrackPreview}
          />
          <ZinePreviewPlayer
            track={currentTrack}
            isPlaying={isPlaying}
            audioRef={audioRef}
            playbackState={playbackState}
            onPlayPause={togglePlayPause}
            onClose={stopPreview}
          />
        </>,
        document.body
      )}
    </div>
  );
};

const SectionHeader = ({ section, crateCount }) => (
  <header className="zine-section-header">
    <div>
      <p>
        {section.dayLabel}
        {section.date ? ` / ${formatSectionDate(section.date)}` : ""}
      </p>
      <h2>{section.title}</h2>
    </div>
    <div className="zine-section-note">
      <span>{section.theme}</span>
      <p>{section.summary}</p>
      <strong>
        {crateCount} {crateCount === 1 ? "mini-crate" : "mini-crates"}
      </strong>
    </div>
  </header>
);

const SourceFilterBar = ({
  sourceOptions,
  selectedSources,
  onSelectedSourcesChange,
  isLoading,
}) => {
  const selectedSourceSet = new Set(selectedSources);
  const isAllSelected = selectedSources.length === 0;

  const toggleSource = (sourceId) => {
    if (selectedSourceSet.has(sourceId)) {
      onSelectedSourcesChange(
        selectedSources.filter((selectedSource) => selectedSource !== sourceId)
      );
      return;
    }

    const nextSourceSet = new Set([...selectedSources, sourceId]);
    const orderedSources = sourceOptions
      .map((sourceOption) => sourceOption.id)
      .filter((optionId) => nextSourceSet.has(optionId));

    onSelectedSourcesChange(orderedSources);
  };

  return (
    <section
      aria-label="Filter mini-crates by source"
      className={`zine-source-filter ${isLoading ? "loading" : ""}`}
    >
      <button
        type="button"
        aria-pressed={isAllSelected}
        data-selected={isAllSelected}
        onClick={() => onSelectedSourcesChange([])}
      >
        All
      </button>

      {sourceOptions.map((sourceOption) => {
        const isSelected = selectedSourceSet.has(sourceOption.id);

        return (
          <button
            className={`source-${sourceOption.id}`}
            data-selected={isSelected}
            aria-label={`Filter by ${sourceOption.label}. ${sourceOption.description}`}
            aria-pressed={isSelected}
            key={sourceOption.id}
            onClick={() => toggleSource(sourceOption.id)}
            title={sourceOption.description}
            type="button"
          >
            <span className="source-short">{sourceOption.shortLabel}</span>
            <span className="source-full">{sourceOption.label}</span>
          </button>
        );
      })}
    </section>
  );
};

const MiniCrateCard = ({
  activeTrackId,
  activeTrackProgress,
  crate,
  index,
  isPlaying,
  onOpenCrate,
  onTrackPreview,
}) => {
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(() => shouldReduceMotion());
  const visibleTracks = crate.tracks.slice(0, 3);
  const hiddenTrackCount = Math.max(0, crate.tracks.length - visibleTracks.length);
  const duration = getCrateDuration(crate);
  const titleId = `${crate.id}-title`;
  const metaItems = [`${crate.tracks.length} tracks`, duration, crate.curator.role].filter(Boolean);
  const coverStyle = {
    "--crate-bg": crate.artwork.palette?.background || "#f4ead2",
    "--crate-fg": crate.artwork.palette?.foreground || "#17130f",
    "--crate-accent": crate.artwork.palette?.accent || "#ff4f8b",
    "--reveal-delay": `${(index % 3) * 90}ms`,
  };

  useEffect(() => {
    const card = cardRef.current;

    if (!card || isVisible) {
      return undefined;
    }

    if (shouldReduceMotion() || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        rootMargin: "-50px 0px",
        threshold: 0.1,
      }
    );

    observer.observe(card);

    return () => observer.disconnect();
  }, [isVisible]);

  const handleCardClick = (event) => {
    if (event.target.closest("button, a")) {
      return;
    }

    onOpenCrate(crate);
  };

  const handleMoreClick = (event) => {
    event.stopPropagation();
    onOpenCrate(crate);
  };

  return (
    <article
      aria-labelledby={titleId}
      className={`zine-crate-card ${isVisible ? "in" : ""} ${isPlaying ? "playing" : ""}`}
      data-source={crate.source}
      onClick={handleCardClick}
      ref={cardRef}
      style={coverStyle}
    >
      <div className="zine-tape" aria-hidden="true" />

      <div className="zine-crate-cover">
        {crate.artwork.url ? (
          <img src={crate.artwork.url} alt={crate.artwork.alt} loading="lazy" />
        ) : (
          <div className="zine-crate-art-fallback" role="img" aria-label={crate.artwork.alt}>
            <span>Mini-crate</span>
            <strong>{crate.title}</strong>
          </div>
        )}

        <span className="zine-source-badge">
          {sourceBadgeLabels[crate.source] || crate.source}
        </span>
        <span className="zine-style-badge">{crate.artwork.style.replaceAll("-", " ")}</span>
        <span className="zine-equalizer" aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
          <i />
        </span>
      </div>

      <div className="zine-crate-body">
        <div className="zine-crate-meta">
          {metaItems.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>

        <h3 id={titleId}>{crate.title}</h3>
        {crate.subtitle ? <p className="zine-crate-subtitle">{crate.subtitle}</p> : null}
        <p className="zine-crate-note">{crate.note}</p>

        <p className="zine-curator">
          Curated by <strong>{crate.curator.name}</strong>
          {crate.curator.handle ? <span> {crate.curator.handle}</span> : null}
        </p>

        <div className="zine-tags" aria-label={`${crate.title} tags`}>
          {crate.tags.slice(0, 4).map((tag) => (
            <span key={tag}>{formatTag(tag)}</span>
          ))}
        </div>

        <ol className="zine-track-list" aria-label={`Tracks in ${crate.title}`}>
          {visibleTracks.map((track, index) => (
            <MiniCrateTrackRow
              crate={crate}
              isActive={activeTrackId === track.id}
              isPlaying={isPlaying && activeTrackId === track.id}
              index={index}
              key={track.id}
              onPreview={onTrackPreview}
              progress={activeTrackId === track.id ? activeTrackProgress : 0}
              track={track}
            />
          ))}
        </ol>

        {hiddenTrackCount > 0 ? (
          <button
            aria-label={`Open ${crate.title} to show ${hiddenTrackCount} more tracks`}
            className="zine-more-count"
            onClick={handleMoreClick}
            type="button"
          >
            +{hiddenTrackCount} more
          </button>
        ) : null}
      </div>
    </article>
  );
};

const MiniCrateTrackRow = ({
  crate,
  index,
  isActive,
  isPlaying,
  onPreview,
  progress,
  track,
}) => {
  const hasPreview =
    track.playback.availability === "preview" && Boolean(track.previewUrl || track.previewEndpoint);
  const externalUrl = getSafeExternalUrl(track.externalUrl);
  const sourceLabel = formatPlatform(track.platform);
  const trackMeta = [
    track.bpm ? `${track.bpm} BPM` : null,
    track.camelotKey || track.musicalKey,
  ].filter(Boolean);
  const unavailableLabel =
    track.playback.reason ||
    (track.playback.availability === "external-only"
      ? `No inline preview. Open ${track.title} on ${sourceLabel}.`
      : `Preview unavailable for ${track.title}.`);
  const handlePreviewClick = (event) => {
    event.stopPropagation();
    onPreview(track, crate);
  };

  return (
    <li className={`zine-track-row ${isActive ? "active" : ""}`}>
      {hasPreview ? (
        <button
          aria-label={
            isPlaying
              ? `Pause ${track.title} by ${track.artist}`
              : isActive
                ? `Play ${track.title} by ${track.artist}`
                : `Preview ${track.title} by ${track.artist}`
          }
          className={`zine-track-play ${isActive ? "active" : ""}`}
          onClick={handlePreviewClick}
          style={{ "--track-progress": `${Math.max(0, Math.min(100, progress || 0))}` }}
          type="button"
        >
          <svg aria-hidden="true" viewBox="0 0 40 40">
            <circle cx="20" cy="20" r="18" />
          </svg>
          <span>
            <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
          </span>
        </button>
      ) : (
        <span className="zine-track-number" title={unavailableLabel}>
          <span aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
          <span className="sr-only">{unavailableLabel}</span>
        </span>
      )}

      <div className="zine-track-copy">
        <GlideText as="p" text={track.title}>
          {track.title}
        </GlideText>
        <GlideText text={track.artist}>{track.artist}</GlideText>
        {track.note ? (
          <GlideText as="small" text={track.note}>
            {track.note}
          </GlideText>
        ) : null}
      </div>

      <div className="zine-track-actions">
        <div className="zine-track-meta" aria-hidden="true">
          {trackMeta.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>

        {externalUrl ? (
          <a
            aria-label={`Open ${track.title} by ${track.artist} on ${sourceLabel}`}
            className="zine-track-external"
            href={externalUrl}
            onClick={(event) => event.stopPropagation()}
            rel="noopener noreferrer"
            target="_blank"
            title={`Open on ${sourceLabel}`}
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </a>
        ) : null}
      </div>
    </li>
  );
};

const ZineCrateModal = ({
  activeTrackId,
  activeTrackProgress,
  crate,
  isPlaying,
  onClose,
  onTrackPreview,
}) => {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!crate) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.body.classList.add("zine-modal-active");
    document.addEventListener("keydown", handleKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      document.body.classList.remove("zine-modal-active");
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [crate, onClose]);

  if (!crate) {
    return null;
  }

  const duration = getCrateDuration(crate);
  const titleId = `${crate.id}-modal-title`;
  const modalStyle = {
    "--crate-bg": crate.artwork.palette?.background || "#f4ead2",
    "--crate-fg": crate.artwork.palette?.foreground || "#17130f",
    "--crate-accent": crate.artwork.palette?.accent || "#ff4f8b",
  };

  return (
    <>
      <div
        aria-hidden="true"
        className="zine-modal-backdrop"
        onMouseDown={onClose}
      />
      <section
        aria-labelledby={titleId}
        aria-modal="true"
        className="zine-crate-modal"
        role="dialog"
        style={modalStyle}
      >
        <button
          aria-label="Close crate details"
          className="zine-modal-close"
          onClick={onClose}
          ref={closeButtonRef}
          type="button"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className="zine-modal-cover" aria-hidden="true">
          {crate.artwork.url ? (
            <img src={crate.artwork.url} alt="" />
          ) : (
            <div className="zine-crate-art-fallback zine-modal-art-fallback">
              <span>Mini-crate</span>
              <strong>{crate.title}</strong>
            </div>
          )}
          <span className="zine-source-badge">
            {sourceBadgeLabels[crate.source] || crate.source}
          </span>
          <span className="zine-style-badge">{crate.artwork.style.replaceAll("-", " ")}</span>
        </div>

        <div className="zine-modal-copy">
          <p className="zine-modal-kicker">Full crate</p>
          <h2 id={titleId}>{crate.title}</h2>
          {crate.subtitle ? <p className="zine-modal-subtitle">{crate.subtitle}</p> : null}
          <p className="zine-modal-note">{crate.note}</p>

          <div className="zine-modal-meta" aria-label={`${crate.title} details`}>
            <span>{crate.tracks.length} tracks</span>
            {duration ? <span>{duration}</span> : null}
            <span>{crate.curator.name}</span>
          </div>

          <div className="zine-tags" aria-label={`${crate.title} tags`}>
            {crate.tags.slice(0, 6).map((tag) => (
              <span key={tag}>{formatTag(tag)}</span>
            ))}
          </div>

          <ol className="zine-track-list zine-modal-track-list" aria-label={`All tracks in ${crate.title}`}>
            {crate.tracks.map((track, index) => (
              <MiniCrateTrackRow
                crate={crate}
                isActive={activeTrackId === track.id}
                isPlaying={isPlaying && activeTrackId === track.id}
                index={index}
                key={track.id}
                onPreview={onTrackPreview}
                progress={activeTrackId === track.id ? activeTrackProgress : 0}
                track={track}
              />
            ))}
          </ol>
        </div>
      </section>
    </>
  );
};

const ZinePreviewPlayer = ({
  audioRef,
  isPlaying,
  onClose,
  onPlayPause,
  playbackState,
  track,
}) => {
  const progressRef = useRef(null);
  const [renderedTrack, setRenderedTrack] = useState(track);
  const [isVisible, setIsVisible] = useState(Boolean(track));

  useEffect(() => {
    let frame;
    let timer;
    const scheduleFrame =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame
        : (callback) => setTimeout(callback, 0);
    const cancelFrame =
      typeof cancelAnimationFrame === "function"
        ? cancelAnimationFrame
        : clearTimeout;

    if (track) {
      setRenderedTrack(track);
      frame = scheduleFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
      timer = setTimeout(() => setRenderedTrack(null), 460);
    }

    return () => {
      if (frame) {
        cancelFrame(frame);
      }

      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [track]);

  useEffect(() => {
    const hasPlayer = Boolean(track || renderedTrack);
    document.body.classList.toggle("zine-preview-active", hasPlayer);

    return () => {
      document.body.classList.remove("zine-preview-active");
    };
  }, [track, renderedTrack]);

  const displayTrack = track || renderedTrack;

  if (!displayTrack) {
    return null;
  }

  const handleSeek = (event) => {
    const audio = audioRef.current;
    const bar = progressRef.current;

    if (!audio || !bar || !Number.isFinite(audio.duration)) {
      return;
    }

    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    audio.currentTime = pct * audio.duration;
  };

  return (
    <div
      className={`zine-nowplaying-player ${isVisible ? "on" : ""}`}
      aria-label="Now playing"
    >
      <div className="zine-nowplaying-disc" aria-hidden="true">
        {displayTrack.artworkUrl ? (
          <img src={displayTrack.artworkUrl} alt="" />
        ) : null}
      </div>

      <div className="zine-nowplaying-info">
        <GlideText
          as="p"
          className="zine-nowplaying-title"
          text={`${displayTrack.artist} / ${displayTrack.title}`}
        >
          <b>{displayTrack.artist}</b>
          <span> / {displayTrack.title}</span>
        </GlideText>
        <GlideText
          as="small"
          className="zine-nowplaying-context"
          text={`${displayTrack.crateTitle ? `${displayTrack.crateTitle} / ` : ""}${
            formatDuration(Math.floor(playbackState.currentTime)) || "0:00"
          } / ${formatDuration(Math.floor(playbackState.duration)) || "0:00"}`}
        >
          {displayTrack.crateTitle ? `${displayTrack.crateTitle} / ` : ""}
          {formatDuration(Math.floor(playbackState.currentTime)) || "0:00"} /{" "}
          {formatDuration(Math.floor(playbackState.duration)) || "0:00"}
        </GlideText>
      </div>

      <button
        aria-label="Seek preview"
        className="zine-nowplaying-progress"
        onClick={handleSeek}
        ref={progressRef}
        type="button"
      >
        <span style={{ width: `${playbackState.progress}%` }} />
      </button>

      <button
        aria-label={isPlaying ? "Pause preview" : "Play preview"}
        className="zine-nowplaying-play"
        onClick={onPlayPause}
        type="button"
      >
        <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
      </button>

      <button
        aria-label="Close preview"
        className="zine-nowplaying-close"
        onClick={onClose}
        type="button"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>
    </div>
  );
};

const LoadingSections = ({ sections }) => {
  const loadingSections = sections?.length ? sections : [loadingSection];

  return (
    <>
      {loadingSections.slice(0, 2).map((section) => (
        <section className="zine-section" key={section.id}>
          <div className="zine-section-header loading">
            <div>
              <span className="zine-skeleton line-small" />
              <span className="zine-skeleton line-large" />
            </div>
          </div>
          <div className="zine-section-grid">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                aria-hidden="true"
                className="zine-crate-card zine-card-skeleton"
                key={`${section.id}-${index}`}
              >
                <span className="zine-skeleton cover" />
                <span className="zine-skeleton line-large" />
                <span className="zine-skeleton line-small" />
                <span className="zine-skeleton line-full" />
                <span className="zine-skeleton line-full" />
              </div>
            ))}
          </div>
        </section>
      ))}
    </>
  );
};

const FilterBarSkeleton = () => (
  <div aria-hidden="true" className="zine-source-filter skeleton">
    {["All", "SB-VIBE", "Staff", "Guest"].map((label) => (
      <span key={label} />
    ))}
  </div>
);

const EmptyState = ({ selectedSourceLabel, onClearFilters }) => (
  <div className="zine-state">
    <p>Nothing in {selectedSourceLabel} yet</p>
    <h2>This crate strip is still blank.</h2>
    <button onClick={onClearFilters} type="button">
      Show all sources
    </button>
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="zine-state" role="alert">
    <p>The crate strip slipped</p>
    <h2>Discover is having trouble loading.</h2>
    {message ? <span>{message}</span> : null}
    <button onClick={onRetry} type="button">
      Retry
    </button>
  </div>
);

export default DiscoverZineStrip;
